import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Globe, Code, Copy, CheckCircle, Zap,
  Facebook, Webhook, Info, AlertCircle, Play, Link2, Unlink,
  Settings2, ArrowRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const { user, selectedBusiness } = useAuth();
  const [searchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [trackingCode, setTrackingCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  // Facebook state
  const [fbPages, setFbPages] = useState([]);
  const [fbForms, setFbForms] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loadingFb, setLoadingFb] = useState(false);
  const [mappingModal, setMappingModal] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [fbAccountConnected, setFbAccountConnected] = useState(false);

  const businessId = selectedBusiness?.business_id || user?.business_id;

  useEffect(() => {
    fetchCampaigns();
    fetchFacebookPages();
    
    // Check for OAuth callback
    if (searchParams.get('fb_connected') === 'true') {
      setFbAccountConnected(true);
      toast.success('Facebook connected successfully!');
      fetchFacebookPages();
    }
    if (searchParams.get('fb_error')) {
      toast.error('Failed to connect Facebook: ' + searchParams.get('fb_error'));
    }
  }, [businessId]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = businessId 
        ? `${API_URL}/api/campaigns?business_id=${businessId}`
        : `${API_URL}/api/campaigns`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
        if (data.length > 0 && !selectedCampaign) {
          setSelectedCampaign(data[0].campaign_id);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchFacebookPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessParam = businessId ? `?business_id=${businessId}` : '';
      const response = await fetch(`${API_URL}/api/facebook/pages${businessParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const pages = await response.json();
        setFbPages(pages);
      }
    } catch (error) {
      console.error('Error fetching Facebook pages:', error);
    }
  };

  const connectFacebook = async () => {
    setLoadingFb(true);
    try {
      if (user?.role === 'super_admin' && !businessId) {
        toast.error('Select a business before connecting Facebook');
        return;
      }

      const token = localStorage.getItem('token');
      const businessParam = businessId ? `?business_id=${businessId}` : '';
      const response = await fetch(`${API_URL}/api/auth/facebook/login${businessParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.oauth_url;
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to start Facebook connection');
      }
    } catch (error) {
      toast.error(error.message || 'Error connecting to Facebook');
    } finally {
      setLoadingFb(false);
    }
  };

  const disconnectFacebookPage = async (pageId) => {
    if (!confirm('Are you sure you want to disconnect this page?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/facebook/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Page disconnected');
        fetchFacebookPages();
        setFbForms([]);
        setSelectedPage(null);
      }
    } catch (error) {
      toast.error('Failed to disconnect page');
    }
  };

  const loadPageForms = async (page) => {
    setSelectedPage(page);
    setLoadingFb(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/facebook/pages/${page.page_id}/forms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setFbForms(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoadingFb(false);
    }
  };

  const openMappingModal = (form) => {
    setMappingModal(form);
    setFieldMappings(form.mapping?.field_mappings || {
      name: 'full_name',
      email: 'email',
      phone: 'phone_number'
    });
  };

  const saveFormMapping = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/facebook/forms/${mappingModal.form_id}/mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          form_name: mappingModal.form_name,
          page_id: selectedPage.page_id,
          business_id: businessId,
          campaign_id: selectedCampaign,
          field_mappings: fieldMappings,
          is_active: true
        })
      });
      
      if (response.ok) {
        toast.success('Form mapping saved!');
        setMappingModal(null);
        loadPageForms(selectedPage);
      }
    } catch (error) {
      toast.error('Failed to save mapping');
    }
  };

  const generateTrackingCode = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign first');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/tracking-code/${selectedCampaign}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrackingCode(data);
        toast.success('Tracking code generated!');
      } else {
        toast.error('Failed to generate tracking code');
      }
    } catch (error) {
      toast.error('Error generating tracking code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Admin access required to manage integrations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Lead Integrations</h1>
        <p className="text-sm sm:text-base text-slate-500">Connect your lead sources and track conversions</p>
      </div>

      <Tabs defaultValue="ghl" className="space-y-4 sm:space-y-6">
        <TabsList className="w-full grid grid-cols-4 h-auto">
          <TabsTrigger value="ghl" className="gap-1.5 py-2.5 text-xs sm:text-sm">
            <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>GHL</span>
          </TabsTrigger>
          <TabsTrigger value="meta" className="gap-1.5 py-2.5 text-xs sm:text-sm">
            <Facebook className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Meta</span>
          </TabsTrigger>
          <TabsTrigger value="website" className="gap-1.5 py-2.5 text-xs sm:text-sm">
            <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Website</span>
            <span className="sm:hidden">Web</span>
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="gap-1.5 py-2.5 text-xs sm:text-sm">
            <Webhook className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>API</span>
          </TabsTrigger>
        </TabsList>

        {/* GHL Webhooks Tab */}
        <TabsContent value="ghl">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Zap className="w-5 h-5 text-amber-500" />
                  GoHighLevel Webhook
                </CardTitle>
                <CardDescription>
                  Use this URL in your GHL Workflow's "Custom Webhook" action to sync leads automatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="p-4 bg-slate-900 rounded-lg">
                  <Label className="text-xs text-slate-400 uppercase tracking-wider">Webhook URL — paste this in GHL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={`${API_URL}/api/webhook/ghl`}
                      readOnly
                      data-testid="ghl-webhook-url"
                      className="font-mono text-sm bg-slate-800 border-slate-700 text-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="copy-ghl-webhook-url"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 shrink-0"
                      onClick={() => copyToClipboard(`${API_URL}/api/webhook/ghl`, 'ghl-url')}
                    >
                      {copied === 'ghl-url' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <Alert className="bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900 text-sm">
                    <strong>How it works:</strong> When a GHL workflow fires this webhook, the system reads the contact's <strong>tags</strong> and automatically routes them to the matching campaign below. Make sure the tag is added to the contact <em>before</em> the webhook fires.
                  </AlertDescription>
                </Alert>

                {/* Campaign → Tag Routing Table */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Campaign Tag Routing</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium text-slate-600">Campaign</th>
                          <th className="text-left px-4 py-2.5 font-medium text-slate-600">GHL Tag</th>
                          <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {campaigns.filter(c => c.sync_tag).map(camp => (
                          <tr key={camp.campaign_id} data-testid={`ghl-route-${camp.sync_tag}`}>
                            <td className="px-4 py-3 font-medium">{camp.name}</td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary" className="font-mono text-xs">{camp.sync_tag}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="bg-green-100 text-green-700 border-0">Active</Badge>
                            </td>
                          </tr>
                        ))}
                        {campaigns.filter(c => c.sync_tag).length === 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                              No campaigns with tag routing configured
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Contacts without a matching tag will be skipped. To add a new route, set a <code className="bg-slate-100 px-1 rounded">sync_tag</code> on the campaign.
                  </p>
                </div>

                {/* Expected Payload */}
                <div>
                  <Label className="text-sm font-medium mb-1 block">Expected Payload (POST JSON)</Label>
                  <Textarea readOnly className="font-mono text-xs h-56 mt-1 bg-slate-50" value={`{
  "event_type": "lead_created",
  "location_id": "{{location.id}}",
  "contact_id": "{{contact.id}}",
  "first_name": "{{contact.first_name}}",
  "last_name": "{{contact.last_name}}",
  "name": "{{contact.name}}",
  "email": "{{contact.email}}",
  "phone": "{{contact.phone}}",
  "source": "ghl_meta",
  "workflow_name": "Your Workflow Name"
}`} />
                  <div className="flex justify-end mt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="copy-ghl-payload"
                      className="text-xs"
                      onClick={() => copyToClipboard(`{
  "event_type": "lead_created",
  "location_id": "{{location.id}}",
  "contact_id": "{{contact.id}}",
  "first_name": "{{contact.first_name}}",
  "last_name": "{{contact.last_name}}",
  "name": "{{contact.name}}",
  "email": "{{contact.email}}",
  "phone": "{{contact.phone}}",
  "source": "ghl_meta",
  "workflow_name": "Your Workflow Name"
}`, 'ghl-payload')}
                    >
                      {copied === 'ghl-payload' ? <CheckCircle className="w-4 h-4 text-green-400 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      Copy Payload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Meta/Facebook Tab */}
        <TabsContent value="meta">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  Facebook & Instagram Lead Ads
                </CardTitle>
                <CardDescription>
                  Connect your Facebook Pages to automatically sync leads from Lead Ads
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Connected Pages */}
                {fbPages.length > 0 ? (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Connected Pages</Label>
                    {fbPages.map(page => (
                      <div key={page.page_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Facebook className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{page.page_name}</p>
                            <p className="text-xs text-slate-500">Connected {new Date(page.connected_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => loadPageForms(page)}>
                            <Settings2 className="w-4 h-4 mr-1" /> Forms
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => disconnectFacebookPage(page.page_id)}>
                            <Unlink className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      {fbAccountConnected
                        ? 'Connected, no Pages. Your Facebook account is connected, but no manageable Facebook Pages were found for this business. Reconnect Facebook to run page discovery again or switch accounts.'
                        : 'No Facebook Pages connected. Click below to connect your Facebook account and load Pages for this business.'}
                    </AlertDescription>
                  </Alert>
                )}

                <Button 
                  onClick={connectFacebook} 
                  disabled={loadingFb}
                  className="bg-[#1877F2] hover:bg-[#166FE5] text-white"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  {loadingFb
                    ? 'Connecting...'
                    : fbPages.length > 0
                      ? 'Connect Another Page'
                      : fbAccountConnected
                        ? 'Reconnect Facebook'
                        : 'Connect Facebook'}
                </Button>

                {/* Lead Forms for Selected Page */}
                {selectedPage && (
                  <div className="mt-6 pt-4 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Lead Forms for {selectedPage.page_name}</Label>
                      <Button variant="ghost" size="sm" onClick={() => loadPageForms(selectedPage)}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {loadingFb ? (
                      <div className="flex justify-center py-4">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : fbForms.length > 0 ? (
                      <div className="space-y-2">
                        {fbForms.map(form => (
                          <div key={form.form_id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{form.form_name}</p>
                              <p className="text-xs text-slate-500">
                                {form.questions?.length || 0} fields • 
                                {form.mapping ? (
                                  <span className="text-green-600 ml-1">Mapped to {campaigns.find(c => c.campaign_id === form.mapping.campaign_id)?.name || 'campaign'}</span>
                                ) : (
                                  <span className="text-amber-600 ml-1">Not mapped</span>
                                )}
                              </p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => openMappingModal(form)}>
                              <ArrowRight className="w-4 h-4 mr-1" /> Map Fields
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 py-4 text-center">No lead forms found for this page</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TikTok Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Play className="w-5 h-5" />
                  TikTok Lead Ads
                </CardTitle>
                <CardDescription>Connect TikTok to sync leads (Coming Soon)</CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="bg-slate-50 border-slate-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    TikTok integration requires App credentials. Contact support to set up.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Website Tab */}
        <TabsContent value="website">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Link2 className="w-5 h-5 text-cyan-500" />
                  Beluga Forms
                </CardTitle>
                <CardDescription>
                  Beluga form creation now lives on the dedicated Forms page. Use this section only for website tracking and external form integrations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-cyan-50 border-cyan-200">
                  <Info className="h-4 w-4 text-cyan-600" />
                  <AlertDescription className="text-cyan-900">
                    Create and manage Beluga-hosted forms from the Forms page. Use the Website tab here only when you need to connect an existing non-Beluga website form.
                  </AlertDescription>
                </Alert>
                <Button onClick={() => navigate('/forms')} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                  <Link2 className="w-4 h-4 mr-2" />
                  Open Forms Page
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Code className="w-5 h-5 text-cyan-500" />
                  Legacy Website Form Tracking
                </CardTitle>
                <CardDescription>
                  Use this only if you already have your own website form and want Beluga to track submissions manually.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                    <SelectTrigger className="w-full sm:w-[250px]">
                      <SelectValue placeholder="Select campaign" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map(c => (
                        <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={generateTrackingCode} disabled={loading || !selectedCampaign} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                    <Zap className="w-4 h-4 mr-2" />{loading ? 'Generating...' : 'Generate Code'}
                  </Button>
                </div>

                {trackingCode && (
                  <div className="space-y-4 mt-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-sm font-medium">Webhook URL</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={trackingCode.webhook_url} readOnly className="font-mono text-xs" />
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(trackingCode.webhook_url, 'url')}>
                          {copied === 'url' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-sm">Tracking Code (paste in website &lt;head&gt;)</Label>
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(trackingCode.tracking_code, 'tracking')}>
                          {copied === 'tracking' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />} Copy
                        </Button>
                      </div>
                      <Textarea value={trackingCode.tracking_code} readOnly className="font-mono text-xs h-40" />
                    </div>

                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800 text-sm">
                        <strong>Setup:</strong> Add code to &lt;head&gt;, then use <code className="bg-blue-100 px-1 rounded">window.belugaSubmitLead(&#123;name, email, phone&#125;)</code> to submit leads from your existing custom form.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Webhook className="w-5 h-5 text-purple-500" />
                Webhook API
              </CardTitle>
              <CardDescription>Send leads from any source via API</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm">Universal Webhook Endpoint</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={`${API_URL}/api/webhook/website`} readOnly className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${API_URL}/api/webhook/website`, 'webhook')}>
                    {copied === 'webhook' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm">Request Format (POST JSON)</Label>
                <Textarea readOnly className="font-mono text-xs h-48 mt-1" value={`{
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "campaign_id": "${selectedCampaign || 'camp_xxxxx'}",
  "source": "custom",
  "utm_source": "facebook",
  "utm_medium": "paid",
  "utm_campaign": "spring_sale"
}`} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Field Mapping Modal */}
      <Dialog open={!!mappingModal} onOpenChange={() => setMappingModal(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Map Form Fields</DialogTitle>
          </DialogHeader>
          
          {mappingModal && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Form: {mappingModal.form_name}</Label>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Send leads to campaign:</Label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(c => (
                      <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Field Mappings:</Label>
                <p className="text-xs text-slate-500">Map Facebook form fields to Beluga Pro fields</p>
                
                {mappingModal.questions?.map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 p-2 bg-blue-50 rounded text-sm">{q.label || q.key}</div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <Select 
                      value={Object.entries(fieldMappings).find(([k,v]) => v === q.key)?.[0] || ''}
                      onValueChange={(val) => setFieldMappings({...fieldMappings, [val]: q.key})}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Map to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingModal(null)}>Cancel</Button>
            <Button onClick={saveFormMapping} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              Save Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default IntegrationsPage;
